import * as React from "react";

import LabeledControl from "@editor/components/common/designSystem/LabeledControl";

import { useReploProductFileDropZone } from "@/features/products/hooks/useProductFileDropZone";
import { useProductFormEdit } from "@/features/products/hooks/useProductFormEdit";
import { formatFileSize, truncateMiddle } from "@/features/products/utils";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { FileText, Plus, Trash2, X } from "lucide-react";

function ProductFilesDropZone() {
  const { watch, dispatchProductEdit, getValues } = useProductFormEdit();
  const currentFiles = watch("productFiles") ?? [];

  const {
    getRootProps,
    getInputProps,
    isUploading,
    openFileDialog,
    progressByFileId,
    droppedFilesMap,
    cancelUpload,
  } = useReploProductFileDropZone({
    onDropBeforeUpload: (id) => {
      dispatchProductEdit({
        type: "addUploadingFileId",
        payload: {
          fileId: id,
        },
      });
      return null;
    },
    onUploadComplete: ({ url, file, id }) => {
      if (file) {
        const newFile = {
          id: id ?? crypto.randomUUID(),
          url,
          name: file.name.split(".")[0] ?? "",
          sizeBytes: file.size,
          fileExtension: file.name.split(".").at(-1) ?? "",
        };

        dispatchProductEdit({
          type: "changeProperty",
          payload: {
            key: "productFiles",
            value: [...(getValues("productFiles") ?? []), newFile],
          },
        });
      }

      if (id) {
        dispatchProductEdit({
          type: "removeUploadingFileId",
          payload: {
            fileId: id,
          },
        });
      }
    },
    onError: (id) => {
      dispatchProductEdit({
        type: "removeUploadingFileId",
        payload: {
          fileId: id,
        },
      });
    },
    acceptDropAssetType: null,
    allowClickToUpload: true,
    currentFiles,
  });

  const handleRemoveFile = (fileUrl: string) => {
    const latestFiles = getValues("productFiles") ?? [];
    const updatedFiles = latestFiles.filter((file) => file.url !== fileUrl);

    dispatchProductEdit({
      type: "changeProperty",
      payload: {
        key: "productFiles",
        value: updatedFiles,
      },
    });
  };

  const uploadingCount = Object.keys(progressByFileId ?? {}).length;
  const hasContent = Boolean(currentFiles.length > 0 || uploadingCount > 0);

  return (
    <LabeledControl
      label="Files"
      size="base"
      containerClassName="w-full"
      labelActionText={
        hasContent ? (
          <IconButton
            variant="tertiary"
            size="sm"
            icon={<Plus size={14} className="text-muted" />}
            onClick={() => openFileDialog?.()}
            aria-label="Add file"
          />
        ) : undefined
      }
    >
      {hasContent && <input {...getInputProps()} style={{ display: "none" }} />}

      {!hasContent ? (
        <EmptyDropZone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
        />
      ) : (
        <div className="flex flex-col gap-1.5">
          {currentFiles.map((file) => (
            <FileItem
              key={file.id}
              fileUrl={file.url}
              fileName={`${file.name}.${file.fileExtension}`}
              fileSize={file.sizeBytes}
              onRemove={handleRemoveFile}
            />
          ))}
          {isUploading &&
            Object.entries(progressByFileId ?? {}).map(([id, progress]) => {
              const file = droppedFilesMap.get(id);
              const fileName = file?.name ?? "";
              return (
                <UploadingFileItem
                  key={id}
                  progress={progress}
                  fileName={fileName}
                  onCancel={() => cancelUpload(id)}
                />
              );
            })}
        </div>
      )}
    </LabeledControl>
  );
}

const FileItem = ({
  fileUrl,
  fileName: providedFileName,
  fileSize,
  onRemove,
}: {
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  onRemove: (fileUrl: string) => void;
}) => {
  const extractedFileName = React.useMemo(() => {
    try {
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      const segments = pathname.split("/");
      return segments[segments.length - 1] || "File";
    } catch {
      return "File";
    }
  }, [fileUrl]);

  const fileName = providedFileName ?? extractedFileName;

  const fileExtension = React.useMemo(() => {
    const parts = fileName.split(".");
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
  }, [fileName]);

  const formattedFileSize = React.useMemo(() => {
    if (!fileSize) {
      return "";
    }
    return formatFileSize(fileSize);
  }, [fileSize]);

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemove(fileUrl);
  };

  return (
    <div className="relative group flex items-center justify-between gap-4 py-2">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex items-center justify-center w-12 h-12 bg-surface rounded-md">
          <span className="text-sm font-medium text-muted">
            {fileExtension}
          </span>
        </div>
        <div className="flex flex-col justify-center items-start gap-0.5 flex-1 min-w-0">
          <div className="text-sm font-medium text-black w-full min-w-0">
            {truncateMiddle(fileName)}
          </div>
          <div className="text-xs text-muted flex items-center">
            {formattedFileSize}
          </div>
        </div>
      </div>
      <IconButton
        variant="tertiary"
        size="sm"
        icon={<Trash2 size={14} className="text-muted" />}
        onClick={handleDeleteClick}
        aria-label="Delete file"
      />
    </div>
  );
};

const UploadingFileItem = ({
  progress,
  fileName,
  onCancel,
}: {
  progress?: number;
  fileName?: string;
  onCancel?: () => void;
}) => {
  return (
    <div className="relative group flex items-center justify-between gap-4 py-2">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex items-center justify-center w-12 h-12 bg-surface rounded-md">
          <span className="text-sm font-medium text-muted">
            {typeof progress === "number" ? `${progress}%` : "..."}
          </span>
        </div>
        <div className="flex flex-col justify-center items-start gap-0.5 flex-1 min-w-0 w-full">
          <div className="text-sm font-medium text-default w-full min-w-0">
            {fileName ? `${truncateMiddle(fileName, 35)}` : "..."}
          </div>
          <div className="flex items-center w-full">
            <Progress
              value={typeof progress === "number" ? progress : 0}
              className="w-full"
              indicatorClassName="bg-default"
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <div
          className="flex shrink-0 items-center justify-center h-6 w-6 p-[4px] transition duration-300 opacity-70"
          aria-label="Uploading file"
        >
          <Spinner size={14} variant="secondary" />
        </div>
        <IconButton
          variant="tertiary"
          size="sm"
          icon={<X size={14} className="text-muted" />}
          aria-label="Cancel upload"
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

type DropZoneProps = {
  getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
};

const EmptyDropZone = ({ getRootProps, getInputProps }: DropZoneProps) => {
  return (
    <div {...getRootProps()} className="flex flex-col gap-1.5 w-full">
      <input {...getInputProps()} />
      <div className="flex justify-center items-center p-4 gap-3 w-full h-16 bg-sheet rounded cursor-pointer hover:bg-sheet/50 transition-colors border border-dashed border-border">
        <div className="relative w-8 h-8">
          <div className="absolute inset-0 flex justify-center items-center w-6 h-6 bg-sheet border border-border rounded-sm transform -rotate-12">
            <FileText size={8} className="text-muted" />
          </div>
          <div className="absolute top-1 left-1 flex justify-center items-center w-6 h-6 bg-sheet border border-border rounded-sm">
            <FileText size={10} className="text-muted" />
          </div>
        </div>
        <span className="text-sm text-muted text-center">
          Drop your files here, or{" "}
          <span className="font-medium">click to browse</span>
        </span>
      </div>
    </div>
  );
};

export default ProductFilesDropZone;
